import React, { useCallback } from 'react';
import { Calendar, Views, EventProps } from 'react-big-calendar';
import { format, isSameMonth, parseISO } from 'date-fns';
import { calendarLocalizer, calendarMessages } from '../../../../utils/calendarUtils';
import { Reservation, RESERVATION_STATUSES } from '../../../../types/reservation';
import { useReservationTypes } from '../../../../hooks/AppData/useReservationTypes';
import { useOrganizationContext } from '../../../../context/OrganizationContext';
import {
  checkIfCurrentUserMissedAttendance,
  getReservationEventColor,
  ReservationCalendarEvent,
} from '../../../../components/ReservationCalendarEvent/ReservationCalendarEvent';
import { CalendarToolbar } from '../../../../components/CalendarToolbar/CalendarToolbar';
import { createReservationSummaryItems } from '../../../../utils/calendarToolbarUtils';
import { useReservationsCalendar } from '../hooks/useReservationsCalendar';
import { dayPropGetter, getCalendarTimeBounds } from '../utils';
import { ActivityList } from '../../../../components/ActivityTimeline/ActivityList';
import { ActivityTimelineItem } from '../../../../components/ActivityTimeline/ActivityTimeline';
import { ActivityView, ActivityViewToggle } from '../../../../components/ActivityTimeline/ActivityViewToggle';
import { dataFillProps } from '../../../../utils/dataFill';

export const ReservationsTab = ({
  calendar: reservationsCalendar,
  reservationTypeFromId,
  externalUserId,
  orgBranches,
  view,
  onViewChange,
}: {
  calendar: ReturnType<typeof useReservationsCalendar>;
  reservationTypeFromId: ReturnType<typeof useReservationTypes>['reservationTypeFromId'];
  externalUserId: string | undefined;
  orgBranches: ReturnType<typeof useOrganizationContext>['orgBranches'];
  view: ActivityView;
  onViewChange: (view: ActivityView) => void;
}) => {
  const { minTime, maxTime } = getCalendarTimeBounds();

  const eventPropGetter = useCallback(
    (reservation: Reservation) => {
      const reservationType = reservationTypeFromId(reservation.reservationTypeId);
      const { currentUserDidNotAttend } = checkIfCurrentUserMissedAttendance(reservation, externalUserId, reservationType);
      const color = getReservationEventColor(reservation, reservationType, currentUserDidNotAttend);
      const isInQueue = reservation.externalUserStatus === RESERVATION_STATUSES.IN_QUEUE;
      return dataFillProps(color, { opacity: isInQueue ? 0.6 : 1 });
    },
    [reservationTypeFromId, externalUserId],
  );

  const reservations = reservationsCalendar.reservations ?? [];
  const summaryItems = createReservationSummaryItems(reservations);

  // The list shows a month: a week or day calendar view goes back to the month first.
  const changeView = (next: ActivityView) => {
    if (next === 'list' && reservationsCalendar.calendarView !== Views.MONTH)
      reservationsCalendar.handleCalendarViewChange(Views.MONTH);
    onViewChange(next);
  };

  const toggle = (
    <div className='activity-view-bar'>
      <ActivityViewToggle name='reservations-view' value={view} onChange={changeView} />
    </div>
  );

  if (view === 'list') {
    // The answer covers the month grid (with days of the neighbouring months); the list shows the month only.
    const monthReservations = reservations.filter((reservation) =>
      isSameMonth(parseISO(reservation.localStartDate), reservationsCalendar.calendarDate),
    );
    const items = monthReservations.flatMap((reservation): ActivityTimelineItem[] => {
      const reservationType = reservationTypeFromId(reservation.reservationTypeId);
      if (!reservationType) return [];
      const start = parseISO(reservation.localStartDate);
      const { currentUserDidNotAttend } = checkIfCurrentUserMissedAttendance(reservation, externalUserId, reservationType);
      const branch =
        orgBranches && orgBranches.length > 1 ? orgBranches.find((b) => b.id === reservation.branch?.id)?.name : undefined;
      const professional = reservation.user ? `${reservation.user.name} ${reservation.user.surnames}` : undefined;
      let aside: ActivityTimelineItem['aside'];
      if (reservation.externalUserStatus === RESERVATION_STATUSES.CANCELED) aside = { label: 'Cancelada', tone: 'danger' };
      else if (currentUserDidNotAttend) aside = { label: 'No asistió', tone: 'danger' };
      else if (reservation.externalUserStatus === RESERVATION_STATUSES.IN_QUEUE) aside = { label: 'En cola', tone: 'muted' };
      return [
        {
          id: reservation.id,
          start,
          time: `${format(start, 'HH:mm')} – ${format(parseISO(reservation.localEndDate), 'HH:mm')}`,
          title: reservationType.name,
          color: getReservationEventColor(reservation, reservationType, currentUserDidNotAttend),
          details: [reservation.title, professional, branch].filter(
            (detail): detail is string => !!detail && detail !== reservationType.name,
          ),
          aside,
          onSelect: () => reservationsCalendar.openReservationPanel(false, reservation),
        },
      ];
    });
    return (
      <>
        {toggle}
        <ActivityList
          date={reservationsCalendar.calendarDate}
          onNavigate={reservationsCalendar.handleCalendarNavigate}
          items={items}
          summaryItems={createReservationSummaryItems(monthReservations)}
        />
      </>
    );
  }

  return (
    <>
      {toggle}
      <div className='calendar-container'>
        <Calendar
          className='user-reservations-calendar'
          dayLayoutAlgorithm='no-overlap'
          localizer={calendarLocalizer}
          events={reservations}
          startAccessor={(reservation) => parseISO(reservation.localStartDate)}
          endAccessor={(reservation) => parseISO(reservation.localEndDate)}
          step={30}
          timeslots={1}
          messages={calendarMessages}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          view={reservationsCalendar.calendarView}
          date={reservationsCalendar.calendarDate}
          onNavigate={reservationsCalendar.handleCalendarNavigate}
          onView={reservationsCalendar.handleCalendarViewChange}
          max={maxTime}
          min={minTime}
          scrollToTime={(() => {
            const adjustedTime = new Date(new Date().setHours(new Date().getHours() - 1, 0, 0, 0));
            return adjustedTime < minTime ? minTime : adjustedTime;
          })()}
          eventPropGetter={eventPropGetter}
          components={{
            event: (props: EventProps<Reservation>) => {
              const reservationType = reservationTypeFromId(props.event.reservationTypeId);
              if (!reservationType) return null;
              return (
                <ReservationCalendarEvent
                  {...props}
                  reservationType={reservationType}
                  showAttendance
                  externalUserId={externalUserId}
                />
              );
            },
            toolbar: (props) => <CalendarToolbar {...props} summaryItems={summaryItems} />,
            showMore: ({ count }) => (
              <div className='rbc-show-more' style={{ cursor: 'default' }}>
                +{count} más
              </div>
            ),
          }}
          onSelectEvent={(event) => {
            reservationsCalendar.openReservationPanel(false, event);
          }}
          dayPropGetter={dayPropGetter}
        />
      </div>
      {orgBranches && orgBranches.length > 1 && (
        <div className='reservations-calendar-legend'>
          {orgBranches.map((branch) => (
            <div key={branch.id} className='legend-item'>
              <span className='legend-dot' style={{ backgroundColor: branch.color ?? '#000000' }} />
              <span className='legend-name'>{branch.name}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
